<template>
  <div class="filter-sec">
    <div class="filter-bar">
      <div class="filter-row top-row">
        <p class="sec-title">All Message</p>
      </div>
      <div class="message-container">
        <div
          class="message-list"
          v-show="!isMobileView || (isMobileView && !chatVisible)"
        >
          <div class="message-header">
            <div class="message-title">
              <img src="../icons/Dot.svg" alt="message-green-dot" />
              <p>Message</p>
              <!-- <div class="message-badge">
                <p>23</p>
              </div> -->
            </div>
            <div class="edit-profile"  :title="'New Message'">
              <img src="../icons/Featured.svg" alt="New message" />
            </div>
          </div>

          <div class="message-search">
            <img src="../icons/MessageIcon.svg" class="search-icon" alt="Search messages" />
            <input type="text" v-model="search" placeholder="Search" />
          </div>

          <div class="contact-list">
            <div
              class="contact"
              v-for="contact in filteredContacts"
              :key="contact.userID"
              @click="selectContact(contact)"
              :class="{
                selected:
                  selectedContact && selectedContact.userID === contact.userID,
                'has-unread': unreadCounts[contact.userID] > 0,
              }"
            >
              <div class="avatar-container">
                <img
                  :src="contact.imageDetails || defaultImage"
                  class="avatar"
                  alt="Contact avatar"
                />

                <span
                  class="status-dot"
                  :class="{
                    online: isOnline(contact.userID),
                    offline: !isOnline(contact.userID),
                  }"
                ></span>
              </div>

              <div class="contact-info">
                <div class="contact-text">
                  <h4 :class="{ 'unread-username': unreadCounts[contact.userID] > 0 }">{{ contact.userName }}</h4>
                  <p class="last-message">
                    {{
                      contact.lastMessage
                        ? contact.lastMessage
                        : "No messages yet"
                    }}
                  </p>
                </div>

                <span
                  v-if="unreadCounts[contact.userID] > 0"
                  class="badge bg-danger"
                  style="margin-left: 5px"
                >
                  {{ unreadCounts[contact.userID] }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div
          class="message-details"
          v-if="selectedContact"
          v-show="!isMobileView || (isMobileView && chatVisible)"
        >
          <div class="chat-header">
            <img
              v-if="isMobileView"
              src="../icons/back.svg"
              class="back-btn"
              @click="goBack"
              alt="Back"
            />
            <div class="avatar-container">
              <img
                :src="selectedContact.imageDetails || defaultImage"
                class="avatar"
                alt="Selected contact avatar"
              />
              <span
                class="status-dot"
                :class="{
                  online: isOnline(selectedContact.userID),
                  offline: !isOnline(selectedContact.userID),
                }"
              ></span>
            </div>
            <!-- Name over status, the way WhatsApp and Teams both stack a chat
                 header. Side by side, the status was pushed further right the
                 longer the name got, and a long name shoved it out of view. -->
            <div class="chat-header-text">
              <p class="select-username">{{ selectedContact.userName }}</p>
              <div
                class="online-status"
                :class="isOnline(selectedContact.userID) ? 'online' : 'offline'"
              >
                <p>{{ presenceLabel(selectedContact) }}</p>
              </div>
            </div>
          </div>
          <div class="chat-body">
            <div class="chat-messages" ref="chatMessages">
              <div v-for="(group, date) in groupedMessages" :key="date">
                <div class="date-separator">{{ date }}</div>

                <!-- Consecutive messages from one person form a "run": tight
                     spacing inside it, a clear gap between runs, and the avatar
                     and tail only on the run's first bubble — the shape both
                     WhatsApp and Teams use to keep a thread readable. -->
                <div
                  v-for="(message, mi) in group"
                  :key="message.messageId || message._id"
                  class="message"
                  :class="[
                    message.fromUserId === userid
                      ? 'message-outgoing'
                      : 'message-incoming',
                    { 'run-start': isRunStart(group, mi), 'run-end': isRunEnd(group, mi) },
                  ]"
                >
                  <template v-if="message.fromUserId !== userid">
                    <img
                      v-if="isRunStart(group, mi)"
                      :src="selectedContact.imageDetails || defaultImage"
                      class="avatar"
                      alt="Message sender avatar"
                    />
                    <!-- Holds the avatar column open so the rest of the run
                         stays aligned under the first bubble. -->
                    <span v-else class="avatar-spacer" aria-hidden="true"></span>
                  </template>

                  <div class="message-content">
                    <div class="message-text" v-html="formatMessage(message.message)"></div>
                    <div class="message-meta">
                      <span class="message-time">
                        {{ formatDate(message.createdTime || message.sendDate) }}
                      </span>
                      <span
                        v-if="message.fromUserId === userid"
                        class="message-ticks"
                        :class="{
                          delivered: message.isDelivered,
                          read: message.isRead,
                        }"
                      >
                        {{ getTickText(message) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="chat-footer">
            <input
              type="text"
              placeholder="Message"
              class="message-inputbox"
              @keydown.enter="sendMessage"
              v-model="newMessage"
            />
            <img
              src="../icons/messagebtn.svg"
              class="msg-sendbtn"
              @click="sendMessage"
              alt="Send message"
            />
          </div>
        </div>
        <div class="no-chat-container" v-else>
          <img src="../icons/Frame.svg" alt="No Chat" class="no-chat-img" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "@/utils/axiosinstance";
import { connectSocket } from "@/utils/socketClient";
import {
  setActiveConversation,
  startTitleFlash,
  stopTitleFlash,
} from "@/utils/messageNotifier";
import DOMPurify from "dompurify";
import defaultImage from "../icons/cardFrame.png";
import "../../../public/css/main.css";
import { EventBus } from "../../services/EventBus.js";
import { trackPageVisit } from "@/utils/trackPageVisit";

const SUPPORT_USER_ID = "3915ba75-9c56-4053-9401-645e2f10c7da";
const SUPPORT_USER_NAME = "support@renants";

export default {
  data() {
    return {
       search: "",
      contacts: [],
      defaultImage,
      users: [],
      selectedContact: null,
      chatMessages: [],
      newMessage: "",
      userid: null,
      socket: null,
      // [event, handler] pairs this component put on the shared socket, so they
      // can be taken off again when it unmounts.
      socketHandlers: [],
      // Bumped on every conversation fetch so a slow earlier response can be
      // recognised as stale and dropped instead of overwriting a newer one.
      chatFetchSeq: 0,
      subsectionname: [],
      sectionData: [],
      messageRefreshInterval: null,
      unreadPollInterval: null,
      uniqueuserID: [],
      startmessages: null,
      showEmojiPicker: false,
      unreadCounts: {},
      nonActive: "",
      display: false,
      userId: null,
      userName: "",
      sectionname: "",
      showModal: false,
      showScrollButton: false,
      sidebarVisible: true,
      chatVisible: false,
      isMobileView: false,
      // flashInterval / originalTitle used to live here. originalTitle captured
      // document.title at mount, which could itself be a half-flashed
      // "New Message!" — the notifier captures the real title instead.
      previousUnreadCounts: {},
      firstLoad: true,
      // ── Presence ──
      // userIDs holding at least one live socket right now. Seeded from
      // GET /api/presence on load, then kept current by `presence:update`
      // pushes. Not persisted anywhere — presence is a live fact, not state.
      onlineUserIDs: [],
      // userID → ISO timestamp of when they last disconnected, for the
      // "Last seen ..." label. Comes from the contacts payload and is
      // refreshed by presence:update.
      lastSeenAt: {},
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToBottom();
    });
    this.checkScreenSize();
    window.addEventListener("resize", this.checkScreenSize);
    this.userid = localStorage.getItem("userid");

    // Record this visit in the admin Users report (Authenticated tab when
    // logged in, Unauthenticated otherwise) — e.g. "View message" email links.
    trackPageVisit("Messages");

    // Initialize Socket.IO connection for real-time messages
    this.initializeSocket();
    
    // Don't fetch messages on mount - only when a contact is selected
    // Messages will load via selectContact() method with correct userID
    
    if (this.userid) {
      this.fetchUnreadCounts();
      // Enable/disable unread counts polling via environment variable
      const pollEnabled = import.meta.env.VITE_UNREADMESSAGE_POLL_ENABLED !== 'false';
      console.log('Unread counts polling check:', {
        VITE_MESSAGE_POLL_ENABLED: import.meta.env.VITE_UNREADMESSAGE_POLL_ENABLED,
        pollEnabled: pollEnabled
      });
      if (pollEnabled) {
        const unreadPollInterval = import.meta.env.VITE_UNREAD_POLL_INTERVAL || 5000;
        // Keep the handle: this interval used to outlive the component and go on
        // polling (and 401ing) long after the user navigated away.
        this.unreadPollInterval = setInterval(() => {
          this.fetchUnreadCounts();
        }, unreadPollInterval);
      }
    }
    // An expired token can never succeed on retry — stop both pollers instead of
    // hammering the API until the tab closes. See utils/sessionExpiry.js.
    window.addEventListener("session-expired", this.stopPollingOnSessionExpiry);
    this.loadContactsWithMessages();
    this.fetchPresence();
  },
  beforeUnmount() {
    // Drop this page's listeners but leave the connection up — it belongs to the
    // app now. Disconnecting here is what used to knock the user offline the
    // moment they navigated away from Messages.
    if (this.socket) {
      this.socketHandlers.forEach(([event, handler]) => this.socket.off(event, handler));
    }
    this.socketHandlers = [];
    // No thread on screen any more, so every incoming message deserves a sound.
    setActiveConversation(null);
    this.stopPollingOnSessionExpiry();
    window.removeEventListener("session-expired", this.stopPollingOnSessionExpiry);
  },
  computed: {
    filteredContacts() {
      const filtered = this.contacts.filter((contact) =>
        (contact.userName || "")
          .toLowerCase()
          .includes((this.search || "").toLowerCase())
      );

      return filtered.sort((a, b) => {
        // Pin support contact to top
        if (a.isSupportContact && !b.isSupportContact) return -1;
        if (!a.isSupportContact && b.isSupportContact) return 1;

        const unreadA = this.unreadCounts[a.userID] || 0;
        const unreadB = this.unreadCounts[b.userID] || 0;

        if (unreadA > 0 && unreadB === 0) return -1;
        if (unreadA === 0 && unreadB > 0) return 1;

        const timeA = new Date(a.lastMessageTime || 0).getTime();
        const timeB = new Date(b.lastMessageTime || 0).getTime();

        return timeB - timeA;
      });
    },
    groupedMessages() {
      const grouped = this.chatMessages.reduce((acc, message) => {
        const date = this.formatDateHeader(message.createdTime || message.sendDate);
        if (!acc[date]) acc[date] = [];
        acc[date].push(message);
        return acc;
      }, {});
      
      console.log('[groupedMessages] Computed:', {
        totalMessages: this.chatMessages.length,
        groupCount: Object.keys(grouped).length,
        groups: Object.keys(grouped).map(date => ({
          date,
          messageCount: grouped[date].length
        }))
      });
      
      return grouped;
    },
  },
  methods: {
    // ── Presence ────────────────────────────────────────────────────────────
    // Initial snapshot. The socket keeps it current from here on; this also
    // covers the case where the socket can't connect at all.
    async fetchPresence() {
      try {
        const { data } = await axios.get("/api/presence");
        this.onlineUserIDs = Array.isArray(data?.online) ? data.online : [];
      } catch (e) {
        // Presence is decoration — a failure here must not break the inbox.
        console.warn("[presence] snapshot failed", e?.message || e);
      }
    },
    isOnline(userID) {
      return !!userID && this.onlineUserIDs.includes(userID);
    },
    // "Online" / "Last seen 3h ago" / "Offline" when we've never seen them go.
    presenceLabel(contact) {
      if (!contact) return "";
      if (this.isOnline(contact.userID)) return "Online";
      const seen = this.lastSeenAt[contact.userID] || contact.lastSeenAt;
      if (!seen) return "Offline";
      const ts = new Date(seen).getTime();
      if (!Number.isFinite(ts)) return "Offline";
      const mins = Math.floor((Date.now() - ts) / 60000);
      if (mins < 1) return "Last seen just now";
      if (mins < 60) return `Last seen ${mins}m ago`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `Last seen ${hrs}h ago`;
      const days = Math.floor(hrs / 24);
      if (days < 7) return `Last seen ${days}d ago`;
      return `Last seen ${new Date(ts).toLocaleDateString()}`;
    },

    // ── Message runs ────────────────────────────────────────────────────────
    // A run is consecutive messages from the same person, close together in
    // time. Both WhatsApp and Teams break a run after a few quiet minutes even
    // when the sender hasn't changed, so a reply an hour later reads as a new
    // remark rather than a continuation.
    sameRun(a, b) {
      if (!a || !b || a.fromUserId !== b.fromUserId) return false;
      const ta = new Date(a.createdTime || a.sendDate).getTime();
      const tb = new Date(b.createdTime || b.sendDate).getTime();
      if (!ta || !tb) return false;
      return Math.abs(tb - ta) < 5 * 60 * 1000;
    },

    /** First bubble of a run — carries the avatar and the tail. */
    isRunStart(group, index) {
      return !this.sameRun(group[index - 1], group[index]);
    },

    /** Last bubble of a run — carries the gap that separates it from the next. */
    isRunEnd(group, index) {
      return !this.sameRun(group[index], group[index + 1]);
    },

    /** Stable identity for a message, however it reached us. */
    messageKey(message) {
      return message?.messageId || message?._id || null;
    },

    /**
     * One of ours, sent moments ago. Bounded on purpose: a message the server
     * never accepted drops out on the next poll instead of sitting in the
     * thread forever pretending it was delivered.
     */
    isJustSent(message) {
      if (!message || message.fromUserId !== this.userid) return false;
      const sentAt = new Date(message.createdTime || message.sendDate).getTime();
      if (!sentAt) return false;
      return Date.now() - sentAt < 15000;
    },

    /**
     * Add a message to the open conversation unless it is already there.
     *
     * Every path that appends has to go through this. The 3s poll in
     * startMessagePolling() replaces chatMessages with the server's copy of the
     * conversation, so a message that is also appended locally shows up twice
     * until the next poll clears it — which is what made a sent message appear
     * as two for a second or three.
     *
     * Keyed comparison, never a bare `a._id === b._id`: two messages that both
     * lack _id would compare equal (undefined === undefined) and the second one
     * would be silently dropped.
     */
    upsertChatMessage(message) {
      const key = this.messageKey(message);
      if (key && this.chatMessages.some((m) => this.messageKey(m) === key)) {
        return false;
      }

      this.chatMessages.push(message);
      this.chatMessages.sort(
        (a, b) =>
          new Date(a.createdTime || a.sendDate) - new Date(b.createdTime || b.sendDate)
      );
      this.$nextTick(() => {
        this.scrollToBottom();
      });
      return true;
    },

    /**
     * Attach a listener to the shared socket and remember it, so beforeUnmount
     * can detach exactly what this component added. The socket outlives the
     * page now, so anything left behind would fire against a dead component.
     */
    bindSocket(event, handler) {
      this.socket.on(event, handler);
      this.socketHandlers.push([event, handler]);
    },

    initializeSocket() {
      if (!this.userid) {
        console.warn('Cannot initialize socket: userid not available');
        return;
      }

      // The connection belongs to the app (utils/socketClient.js), not to this
      // page — presence is "has a live socket", and owning it here meant the
      // user only appeared online while the Messages tab was open. Borrow it;
      // connectSocket() returns the existing one if it is already up.
      // The server authenticates the handshake with the JWT and ties the socket
      // to our user, so real-time channels can't be subscribed to by others.
      this.socket = connectSocket();
      if (!this.socket) {
        console.warn('Cannot initialize socket: no auth token');
        return;
      }

      this.bindSocket('connect', () => {
        console.log('Socket.IO connected:', this.socket.id);
        // On a reconnect we've missed every presence change that happened
        // while we were away, so ask for the full picture again.
        this.socket.emit('presence:list');
      });

      // Full snapshot, in reply to our presence:list request.
      this.bindSocket('presence:list', (payload) => {
        this.onlineUserIDs = Array.isArray(payload?.online) ? payload.online : [];
      });

      // A single user connected or dropped.
      this.bindSocket('presence:update', ({ userID, online, lastSeenAt }) => {
        if (!userID) return;
        const without = this.onlineUserIDs.filter((id) => id !== userID);
        this.onlineUserIDs = online ? [...without, userID] : without;
        if (lastSeenAt) this.lastSeenAt = { ...this.lastSeenAt, [userID]: lastSeenAt };
      });

      this.bindSocket('connect_error', (error) => {
        console.error('Socket.IO connection error:', error);
      });

      // Listen for incoming messages directed to this user
      const messageChannel = `receiveMessage:${this.userid}`;
      console.log('Listening for messages on channel:', messageChannel);
      
      this.bindSocket(messageChannel, (message) => {
        console.log('[Socket.IO] Received real-time message:', {
          messageId: message.messageId,
          from: message.fromUserId,
          to: message.toUserId,
          text: message.message?.substring(0, 30)
        });
        
        // Add message to chat if conversation is open with the sender
        if (this.selectedContact && message.fromUserId === this.selectedContact.userID) {
          console.log('[Socket.IO] Message is from selected contact, adding to chat');
          
          message.isDelivered = true;
          if (this.upsertChatMessage(message)) {
            console.log('[Socket.IO] Message added to chat, total messages:', this.chatMessages.length);

            // Mark delivered when receiver gets the message in-app.
            this.markMessageDelivered(message);

            // If chat is open with sender, mark read immediately.
            this.markMessageRead(message);
          } else {
            console.log('[Socket.IO] Message already exists, skipping duplicate');
          }
        } else {
          console.log('[Socket.IO] Message not from selected contact, updating sidebar only');

          // Receiver got it, but chat is not open: delivered only.
          this.markMessageDelivered(message);
        }
        
        // Always update contact list to show new last message
        this.loadContactsWithMessages();
        
        // Always update unread counts
        this.fetchUnreadCounts();
        
        // The sound and the title flash are handled app-wide by
        // utils/messageNotifier.js, which hears this same event on any page —
        // doing it here as well would double the sound whenever the Messages
        // page happened to be the one open.
      });

      this.bindSocket('disconnect', (reason) => {
        console.log('Socket.IO disconnected:', reason);
      });

      this.bindSocket(`messageDelivered:${this.userid}`, (payload) => {
        this.applyDeliveryReceipt(payload);
      });

      this.bindSocket(`messageRead:${this.userid}`, (payload) => {
        this.applyReadReceipt(payload);
      });
    },

    // Title flashing lives in utils/messageNotifier.js so this page and the
    // app-wide notifier can't run two intervals against document.title at once.
    // These stay as thin wrappers because several call sites below use them.
    startFlashingTab(label) {
      startTitleFlash(label);
    },

    stopFlashingTab() {
      stopTitleFlash();
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const chatContainer = this.$refs.chatMessages;
        if (chatContainer) {
          chatContainer.scrollTop = chatContainer.scrollHeight;
        }
      });
    },
    checkScreenSize() {
      this.isMobileView = window.innerWidth < 768;
    },
    
    // sendMessage() {
    //   if (!this.newMessage.trim() || !this.selectedContact) {
    //     console.warn("No contact selected or message is empty");
    //     return;
    //   }

    //   const message = {
    //     id: Date.now(),
    //     sender: "me",
    //     message: this.newMessage,
    //     sendDate: new Date().toISOString(),
    //     isRead: false,
    //     toUserId: this.selectedContact.userID,
    //     fromUserId: this.userid,
    //   };

    //   // Push message immediately for UI responsiveness
    //   this.chatMessages.push(message);
    //   // Sort messages by sendDate ascending (oldest first)
    //   this.chatMessages.sort((a, b) => new Date(a.sendDate) - new Date(b.sendDate));
    //   this.scrollToBottom();

    //   // Update the contact's last message and time immediately in the contacts list
    //   const contactIdx = this.contacts.findIndex(
    //     (c) => c.userID === this.selectedContact.userID
    //   );
    //   if (contactIdx !== -1) {
    //     this.contacts[contactIdx].lastMessage = this.newMessage;
    //     this.contacts[contactIdx].lastMessageTime = message.sendDate;
    //     console.log('[SEND] Updated contact lastMessage:', this.contacts[contactIdx]);
    //   }

    //   // Send message to server
    //   axios
    //     .post("/api/message", message)
    //     .then((response) => {
    //       console.log(response);
    //     })
    //     .catch((error) => {
    //       console.error("Error sending message:", error);
    //     });

    //   this.newMessage = "";

    //   this.onupdatemessage();
    //   // Optionally, reload contacts after a short delay to sync with server
    //   setTimeout(() => {
    //     this.loadContactsWithMessages();
    //   }, 500);
    //   this.$nextTick(() => {
    //     const el = this.$el.querySelector(".chatbox-messages");
    //     if (el) {
    //       el.scrollTop = el.scrollHeight;
    //     }
    //   });
    // },

    sendMessage() {
      if (!this.newMessage.trim() || !this.selectedContact) {
        console.warn('[sendMessage] Cannot send: empty message or no contact selected');
        return;
      }

      const messageData = {
        fromUserId: this.userid,
        toUserId: this.selectedContact.userID,
        message: this.newMessage
      };

      // Clear input immediately so next message can be typed without delay
      this.newMessage = "";


      console.log('[sendMessage] Sending message:', {
        from: messageData.fromUserId,
        to: messageData.toUserId,
        text: messageData.message.substring(0, 30)
      });

      axios.post("/api/message", messageData)
      .then(res => {
        const newMsg = {
          ...res.data.item,
          isDelivered: res.data.item?.isDelivered || false,
          isRead: res.data.item?.isRead || false,
        };
        console.log('[sendMessage] Message sent successfully:', newMsg.messageId);

        // Add to local chat immediately — unless a poll that fired between the
        // save and this response already brought it back from the server, in
        // which case appending it here is what produced the doubled message.
        this.upsertChatMessage(newMsg);

        // Update contact list with new last message
        this.loadContactsWithMessages();
        
        // Emit socket event to notify receiver in real-time
        if (this.socket && this.socket.connected) {
          console.log('[sendMessage] Emitting via Socket.IO to receiver:', messageData.toUserId);
          this.socket.emit('sendMessage', newMsg);
        } else {
          console.warn('[sendMessage] Socket not connected, receiver will get message via polling');
        }
      })
      .catch(error => {
        console.error('[sendMessage] Error sending message:', error);
      });
    },

    onupdatemessage() {
      //console.log("walid", this.selectedContact.userID);
      axios
        .put("/api/updatemessage", {
          fromUserId: this.selectedContact.userID,
          toUserId: this.userid,
        })
        .then((response) => {
          console.log(response);
          this.fetchUnreadCounts();
        })
        .catch((error) => {
          console.log(error);
        });
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },
    getTickText(message) {
      if (message?.isRead) return "\u2713\u2713";
      if (message?.isDelivered) return "\u2713\u2713";
      return "\u2713";
    },
    applyDeliveryReceipt(payload) {
      if (!payload) return;

      this.chatMessages = this.chatMessages.map((msg) => {
        const isSameMessage = payload.messageId && msg.messageId === payload.messageId;
        const isSameConversation =
          !payload.messageId &&
          msg.fromUserId === this.userid &&
          msg.toUserId === payload.toUserId &&
          !msg.isRead;

        if (isSameMessage || isSameConversation) {
          return {
            ...msg,
            isDelivered: true,
            deliveredTime: payload.deliveredTime || msg.deliveredTime || new Date().toISOString(),
          };
        }

        return msg;
      });
    },
    applyReadReceipt(payload) {
      if (!payload) return;

      this.chatMessages = this.chatMessages.map((msg) => {
        const isSameMessage = payload.messageId && msg.messageId === payload.messageId;
        const isSameConversation =
          !payload.messageId &&
          msg.fromUserId === this.userid &&
          msg.toUserId === payload.toUserId;

        if (isSameMessage || isSameConversation) {
          return {
            ...msg,
            isDelivered: true,
            isRead: true,
            deliveredTime: payload.deliveredTime || msg.deliveredTime || new Date().toISOString(),
            readTime: payload.readTime || msg.readTime || new Date().toISOString(),
          };
        }

        return msg;
      });
    },
    markMessageDelivered(message) {
      if (!message?.messageId || !message?.fromUserId || !message?.toUserId) return;

      axios
        .put("/api/updatemessagedelivery", {
          messageId: message.messageId,
          fromUserId: message.fromUserId,
          toUserId: this.userid,
        })
        .then(() => {
          if (this.socket && this.socket.connected) {
            this.socket.emit("messageDelivered", {
              messageId: message.messageId,
              fromUserId: message.fromUserId,
              toUserId: this.userid,
              deliveredTime: new Date().toISOString(),
            });
          }
        })
        .catch((error) => {
          console.error("[markMessageDelivered] Error:", error);
        });
    },
    markMessageRead(message) {
      if (!message?.messageId || !message?.fromUserId || !message?.toUserId) return;
      if (!this.selectedContact || message.fromUserId !== this.selectedContact.userID) return;

      axios
        .put("/api/updatemessage", {
          messageId: message.messageId,
          fromUserId: message.fromUserId,
          toUserId: this.userid,
        })
        .then(() => {
          if (this.socket && this.socket.connected) {
            this.socket.emit("messageRead", {
              messageId: message.messageId,
              fromUserId: message.fromUserId,
              toUserId: this.userid,
              deliveredTime: new Date().toISOString(),
              readTime: new Date().toISOString(),
            });
          }
        })
        .catch((error) => {
          console.error("[markMessageRead] Error:", error);
        });
    },
    markConversationDelivered(otherUserId) {
      if (!otherUserId || !this.userid) return;

      axios
        .put("/api/updatemessagedelivery", {
          fromUserId: otherUserId,
          toUserId: this.userid,
        })
        .then((res) => {
          if ((res.data?.modifiedCount || 0) > 0 && this.socket && this.socket.connected) {
            this.socket.emit("messageDelivered", {
              fromUserId: otherUserId,
              toUserId: this.userid,
              deliveredTime: new Date().toISOString(),
            });
          }
        })
        .catch((error) => {
          console.error("[markConversationDelivered] Error:", error);
        });
    },
    formatDateHeader(date) {
      const options = { year: "numeric", month: "long", day: "numeric" };
      return new Date(date).toLocaleDateString([], options);
    },

    formatMessage(message) {
      if (!message || typeof message !== "string") return "";

      // 1. Escape HTML entities to neutralise any injected tags
      const escaped = message
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

      // 2. Convert newlines
      let result = escaped.replace(/\n/g, "<br>");

      // 3. Markdown-style [text](url)
      const mdLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
      result = result.replace(
        mdLinkRegex,
        '<a href="$2" target="_blank" rel="noopener noreferrer" class="chat-link">$1</a>'
      );

      // 4. Plain URLs (not already inside an href)
      const plainLinkRegex = /(?<!href=")(https?:\/\/[^\s<]+)/g;
      result = result.replace(
        plainLinkRegex,
        '<a href="$1" target="_blank" rel="noopener noreferrer" class="chat-link">$1</a>'
      );

      // 5. Final sanitisation pass — allow only safe tags/attributes
      return DOMPurify.sanitize(result, {
        ALLOWED_TAGS: ["a", "br"],
        ALLOWED_ATTR: ["href", "target", "rel", "class"],
      });
    },
    selectContact(contact) {
      console.log('[selectContact] Switching to contact:', contact.userName);
      
      this.selectedContact = contact;

      // Reset the unread badge instantly for UI
      if (this.unreadCounts[contact.userID]) {
        this.unreadCounts[contact.userID] = 0;
        console.log('[selectContact] Cleared unread count for contact:', contact.userName);
      }

      // Reading this thread: the notifier stays quiet for this person while the
      // tab is focused, and the title goes back to normal.
      setActiveConversation(contact.userID);
      this.stopFlashingTab();

      // Fetch chat messages for the selected contact
      this.fetchChatMessages(contact.userID);

      if (this.isMobileView) {
        this.chatVisible = true;
      }

      // Mark messages as read on the server after 500ms
      setTimeout(() => {
        console.log('[selectContact] Marking messages as read from:', contact.userID, 'to:', this.userid);
        axios
          .put("/api/updatemessage", {
            fromUserId: contact.userID,   // sender (who sent the messages)
            toUserId: this.userid,        // receiver (current user reading them)
          })
          .then(() => {
            console.log('[selectContact] Marked messages as read');
            if (this.socket && this.socket.connected) {
              this.socket.emit("messageRead", {
                fromUserId: contact.userID,
                toUserId: this.userid,
                deliveredTime: new Date().toISOString(),
                readTime: new Date().toISOString(),
              });
            }
            // Refresh unread counts after updating
            this.fetchUnreadCounts();
            // Notify Navigation.vue to refresh its unread count
            EventBus.notifyMessageRead();
            console.log('[selectContact] EventBus notified and fetchUnreadCounts called');
          })
          .catch((error) => {
            console.error('[selectContact] Error marking as read:', error);
          });
      }, 500);

      // Stop flashing tab immediately
      this.stopFlashingTab();

      // Start polling for messages every 3 seconds
      this.startMessagePolling(contact.userID);
    },

    startMessagePolling(userID) {
      // Clear any existing interval
      if (this.messageRefreshInterval) {
        clearInterval(this.messageRefreshInterval);
      }

      // Check if polling is enabled via environment variable (default: true)
      const pollEnabled = import.meta.env.VITE_MESSAGE_POLL_ENABLED !== 'false';
      console.log('Message polling check:', {
        VITE_MESSAGE_POLL_ENABLED: import.meta.env.VITE_MESSAGE_POLL_ENABLED,
        pollEnabled: pollEnabled
      });
      if (!pollEnabled) {
        console.log('Message polling disabled via VITE_MESSAGE_POLL_ENABLED');
        return; // Polling disabled, exit early
      }

      // Set up the interval to fetch messages (3 seconds for real-time updates)
      const pollInterval = 3000;
      console.log('Message polling interval set to:', pollInterval, 'ms');
      this.messageRefreshInterval = setInterval(() => {
        console.log('[Polling] Refreshing messages for:', userID);
        this.fetchChatMessages(userID);
        this.fetchUnreadCounts();
        this.loadContactsWithMessages();
      }, pollInterval);
    },
    goBack() {
      this.chatVisible = false;
      this.selectedContact = null;
      setActiveConversation(null);
    },

    // fetchChatMessages(userID) {
    //   const currentUserId = this.userid;
    //   console.log("fetchmssage", userID);
    //   // console.log("from", currentUserId);
    //   //console.log("to", userID);

    //  axios
    //     .post("/api/messagelist", {
    //       fromUserId: currentUserId,
    //       toUserId: userID,
    //     })
    //     .then((response) => {
    //       this.chatMessages = response.data.items
    //         .filter(
    //           (message) =>
    //             (message.fromUserId === currentUserId &&
    //               message.toUserId === userID) ||
    //             (message.fromUserId === userID &&
    //               message.toUserId === currentUserId)
    //         )
    //         .map((message) => {
    //           message.sender =
    //             message.fromUserId === currentUserId ? "me" : "other";
    //           return message;
    //         });
    //         // Sort messages by sendDate ascending (oldest first)
    //         this.chatMessages.sort((a, b) => new Date(a.sendDate) - new Date(b.sendDate));
    //     })
    //     .catch((error) => {
    //       console.error("Error fetching messages:", error);
    //     });
    // },
    fetchChatMessages(userID) {
      if (!userID || !this.userid) {
        console.warn('[fetchChatMessages] Missing userID or userid');
        return;
      }

      const currentUserId = this.userid;
      const seq = ++this.chatFetchSeq;

      console.log('[fetchChatMessages] Fetching conversation (both directions):', {
        currentUserId,
        otherUserId: userID
      });

      // Backend returns messages in BOTH directions automatically
      // (fromUserId=A to toUserId=B) OR (fromUserId=B to toUserId=A)
      axios.post("/api/messagelist?limit=1000&sortBy={\"createdTime\":1}", {
        fromUserId: currentUserId,
        toUserId: userID
      })
      .then(res => {
        // Responses can come back out of order, and the user may have switched
        // conversations while this one was in flight. Either way the answer is
        // stale: applying it would show the wrong thread, or undo a newer one.
        if (seq !== this.chatFetchSeq) {
          console.log('[fetchChatMessages] Stale response ignored (superseded)');
          return;
        }
        if (!this.selectedContact || this.selectedContact.userID !== userID) {
          console.log('[fetchChatMessages] Stale response ignored (contact changed)');
          return;
        }

        const newMessages = res.data.items || [];

        console.log('[fetchChatMessages] Received response:', {
          messageCount: newMessages.length,
          totalCount: res.data.totalCount
        });

        // A poll already in flight when a message was saved comes back without
        // it, and a blind replace would make the message the user just sent
        // blink out until the next poll. Carry over anything of our own that
        // the snapshot simply predates.
        const serverKeys = new Set(
          newMessages.map((m) => this.messageKey(m)).filter(Boolean)
        );
        const justSent = this.chatMessages.filter((m) => {
          const key = this.messageKey(m);
          return key && !serverKeys.has(key) && this.isJustSent(m);
        });

        // Sort by createdTime ascending (oldest first)
        this.chatMessages = [...newMessages, ...justSent].sort((a, b) => {
          return new Date(a.createdTime || a.sendDate) - new Date(b.createdTime || b.sendDate);
        });

        this.markConversationDelivered(userID);

        console.log('[fetchChatMessages] Conversation updated:', {
          count: this.chatMessages.length,
          currentUserId: this.userid,
          lastMessage: this.chatMessages[this.chatMessages.length - 1] ? {
            from: this.chatMessages[this.chatMessages.length - 1].fromUserId,
            to: this.chatMessages[this.chatMessages.length - 1].toUserId,
            isOutgoing: this.chatMessages[this.chatMessages.length - 1].fromUserId === this.userid,
            text: this.chatMessages[this.chatMessages.length - 1].message?.substring(0, 30)
          } : null
        });

        this.$nextTick(() => {
          this.scrollToBottom();
        });
      })
      .catch(error => {
        console.error('[fetchChatMessages] Error:', error);
      });
    },

    stopPollingOnSessionExpiry() {
      if (this.messageRefreshInterval) {
        clearInterval(this.messageRefreshInterval);
        this.messageRefreshInterval = null;
      }
      if (this.unreadPollInterval) {
        clearInterval(this.unreadPollInterval);
        this.unreadPollInterval = null;
      }
    },

    fetchUnreadCounts() {
      if (!this.userid) return;
      axios
        .post("/api/messagelist?limit=10000", {
          toUserId: this.userid,
          isRead: false,
        })
        .then((response) => {
          const unreadMessages = response.data.items;
          
          console.log('[fetchUnreadCounts] Unread messages:', {
            count: unreadMessages.length,
            messages: unreadMessages.map(m => ({
              from: m.fromUserId,
              text: m.message?.substring(0, 20)
            }))
          });

          this.unreadCounts = unreadMessages.reduce((counts, message) => {
            const contactId = message.fromUserId;
            if (!counts[contactId]) counts[contactId] = 0;
            counts[contactId]++;
            return counts;
          }, {});

          console.log('[fetchUnreadCounts] Updated unreadCounts:', this.unreadCounts);

          const newUnreadCounts = unreadMessages.reduce((counts, message) => {
            const contactId = message.fromUserId;
            if (!counts[contactId]) counts[contactId] = 0;
            counts[contactId]++;
            return counts;
          }, {});

          // Compare new counts with previous counts
          if (!this.firstLoad) {
          for (const contactId in newUnreadCounts) {
            if (
              !this.previousUnreadCounts[contactId] ||
              newUnreadCounts[contactId] > this.previousUnreadCounts[contactId]
            ) {
              const totalUnread = Object.values(newUnreadCounts).reduce(
                (a, b) => a + b,
                0
              );

              // Hand the count to the flasher rather than writing the title
              // directly — its interval would overwrite the assignment a second
              // later, so the count used to blink away and never come back.
              if (totalUnread > 0) {
                this.startFlashingTab(
                  `(${totalUnread}) New message${totalUnread > 1 ? "s" : ""}`
                );
              } else {
                this.stopFlashingTab();
              }
            }
          }
        }
        this.firstLoad = false;

          // Update previous counts
          this.previousUnreadCounts = { ...newUnreadCounts };

          // Assign newUnreadCounts to this.unreadCounts to update the view
          this.unreadCounts = newUnreadCounts;
        })
        .catch((error) => {
          console.error('[fetchUnreadCounts] Error fetching:', error);
        });
    },
 
loadContactsWithMessages() {
  console.log('[loadContactsWithMessages] Fetching all contacts with messages');
  
  axios.post("/api/messagelist?limit=10000&sortBy={\"sendDate\":-1}", {
    userId: this.userid
  })
  .then(res => {
    console.log('[loadContactsWithMessages] Received messages:', {
      totalMessages: res.data.items?.length || 0
    });

    const messages = res.data.items;
    const contactMap = {};

    // Messages are sorted by sendDate descending (newest first)
    // So the first message we encounter for each contact is the latest
    messages.forEach(msg => {
      const otherUser =
        msg.fromUserId === this.userid
          ? msg.toUserId
          : msg.fromUserId;

      // Only set if not already set (first encounter = latest message)
      if (!contactMap[otherUser]) {
        contactMap[otherUser] = msg;
      }
    });

    console.log('[loadContactsWithMessages] Contact map created:', {
      uniqueContacts: Object.keys(contactMap).length,
      contactsWithLastMessage: Object.keys(contactMap).map(userId => ({
        userId,
        lastMessage: contactMap[userId].message?.substring(0, 30),
        sendDate: contactMap[userId].sendDate
      }))
    });

    axios.post("/api/userlogcustom")
      .then(userRes => {

        this.contacts = userRes.data.items
          .filter(user => contactMap[user.userID])
          .map(user => ({
            userID: user.userID,
            userName: user.userName,
            // NOTE: deliberately no `status` here. This used to be the user's
            // ACCOUNT status ("active" = account enabled), which the dot then
            // rendered as "Online" — so every enabled account showed green
            // forever. Presence now comes from onlineUserIDs / isOnline().
            imageDetails: null,
            lastSeenAt: user.lastSeenAt || null,
            lastMessage: contactMap[user.userID].message,
            lastMessageTime: contactMap[user.userID].sendDate
          }))
          .sort((a, b) =>
            new Date(b.lastMessageTime) -
            new Date(a.lastMessageTime)
          );

        // ── Inject support contact as default first contact ──
        if (this.userid !== SUPPORT_USER_ID) {
          const existingSupport = this.contacts.find(
            (c) => c.userID === SUPPORT_USER_ID
          );
          if (existingSupport) {
            existingSupport.isSupportContact = true;
          } else {
            this.contacts.push({
              userID: SUPPORT_USER_ID,
              userName: SUPPORT_USER_NAME,
              imageDetails: null,
              lastMessage: "Welcome to Renants! How can we help?",
              lastMessageTime: null,
              isSupportContact: true,
            });
          }
        }
        // ─────────────────────────────────────────────────────

        console.log('[loadContactsWithMessages] Contacts loaded:', {
          count: this.contacts.length,
          contacts: this.contacts.map(c => c.userName)
        });
      })
      .catch(error => {
        console.error('[loadContactsWithMessages] Error fetching users:', error);
      });

  })
  .catch(error => {
    console.error('[loadContactsWithMessages] Error fetching messages:', error);
  });
}


  },
};
</script>

<style scoped>
@media (max-width: 768px) {
  .chat-messages {
    scrollbar-width: none; /* Firefox */
  }
  .chat-messages::-webkit-scrollbar {
    display: none; /* Chrome, Safari */
  }
  .no-chat-container {
    display: none !important;
  }
  .filter-sec {
    margin: 20px auto !important;
    padding: 0 16px !important;
  }
  .message-container {
    flex-direction: column;
    padding: 0;
    /* !important on border/height: the base .message-container rule sits LATER
       in this stylesheet (border: 1px solid; height: 740px) at equal
       specificity, and a media query adds none — so without it the base wins
       and re-applies both here. The height especially: top + bottom + height
       over-constrains a fixed box, CSS drops bottom, and the panel becomes
       740px tall — pushing the composer below the visible viewport. */
    border: none !important;
    /* App-like chat: pin the panel between the 56px navbar (plus notch inset)
       and the bottom of the viewport, so the composer is always on screen.
       In page flow the "All Message" heading above and the site footer below
       forced the user to scroll to reach the input; fixed positioning takes
       the chat out of that flow and covers them. */
    position: fixed;
    left: 0;
    right: 0;
    top: calc(56px + env(safe-area-inset-top, 0px));
    bottom: env(safe-area-inset-bottom, 0px);
    height: auto !important;
    background: #fff;
    z-index: 900; /* under the navbar (5000) and tab bar (1000) */
  }

  .message-list {
    width: 100% !important;
    height: 100% !important;
    border-right: none !important;
    padding-bottom: 0;
  }

  .message-details {
    width: 100%;
    height: 100%;
    border-left: none;
  }

  .back-btn {
    width: 24px;
    height: 24px;
    margin-right: 12px;
    cursor: pointer;
    padding:0px !important
  }
  .message-header {
    width: 100% !important;
  }
  .message-search {
    max-width: none !important;
  }
  .message-search input {
    padding-left: 42px !important;
  }
  .message-search .search-icon {
    left: 24px !important;
  }
  .chat-header {
  padding: 16px 02px !important;
}

}

/* Below 600px the fixed bottom tab bar (48px + home-indicator inset) is
   visible — stop the pinned chat above it so the tabs stay usable. */
@media (max-width: 600px) {
  .message-container {
    bottom: calc(48px + env(safe-area-inset-bottom, 0px));
  }
}

@media (min-width: 300px) and (max-width: 400px) {
    .chat-footer {
        gap: 6px !important;      /* remove leading zero */
        padding: 15px 0 !important;
    }
    /* The status used to be hidden here because the pill had nowhere to go
       beside the name. Stacked under it, it fits — and knowing whether the
       person is online matters most on a phone. */
    .chat-header {
      padding: 12px 14px !important;
      gap: 8px !important;
    }
}


.no-chat-container {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  text-align: center;
  background: #fff;
  border-radius: 0 16px 16px 0;
}
.no-chat-img {
  width: 500px;
  height: 500px;
  object-fit: contain;
}
.msg-sendbtn {
  cursor: pointer;
}
.contact-text h4 {
  font-family: var(--font-primary);
  font-weight: var(--weight-medium);
  font-size: 14px;
  line-height: 20px;
  color: #181d27;
}

.contact-text h4.unread-username {
  color: #1a73e8;
  font-weight: 700;
}
.last-message {
  color: #888;
  font-size: 13px;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 160px;
}
.contact.selected {
  background-color: #f5f5f5;
  border: 2px solid #d0d0d0;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.chat-footer img {
  width: 44px;
  height: 44px;
}
.message-inputbox {
  flex: 1;
  height: 42px;
  border-radius: 22px;
  border: 1.5px solid #e5e9f2;
  padding: 10px 16px;
  font-size: 14px;
  font-family: "Inter", sans-serif;
  color: #181d27;
  background: #f8f9fc;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.message-inputbox:focus {
  border-color: #3467d5;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(52, 103, 213, 0.10);
}

.chat-footer {
  width: 100%;
  padding: 12px 20px;
  border-top: 1px solid #f0f1f5;
  display: flex;
  align-items: center;
  gap: 10px;
  box-sizing: border-box;
  background: #fff;
}

.chat-footer img.msg-sendbtn {
  width: 40px;
  height: 40px;
  cursor: pointer;
  border-radius: 50%;
  transition: transform 0.15s ease, filter 0.15s ease;
}

.chat-footer img.msg-sendbtn:hover {
  transform: scale(1.08);
  filter: brightness(1.1);
}
/* Name and status stack in one column beside the avatar, so the status sits at
   a fixed place instead of drifting right with the length of the name. */
.chat-header-text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;          /* lets a long name ellipsis instead of pushing the
                            rest of the header out of the panel */
}

.select-username {
  color: #181d27;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* A quiet status line rather than a pill. As a pill it was a second heavy
   element competing with the name; underneath, it reads as a caption. */
.online-status {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
}

.online-status.online::before {
  content: "";
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #22c55e;
  flex-shrink: 0;
}

.online-status p {
  color: #6b7280;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  font-family: "Inter", sans-serif;
  margin: 0;
  letter-spacing: 0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.online-status.online p {
  color: #15803d;
}

.message-details {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
}
.message-search {
  position: relative;
  width: 100%;
  max-width: 100%;
  padding: 8px 12px;
  box-sizing: border-box;
}

.message-search input {
  width: 100%;
  padding-left: 42px;
  height: 36px;
  border: 1.5px solid #e5e9f2;
  border-radius: 20px;
  outline: none;
  background: #ffffff;
  font-size: 13px;
  font-family: "Inter", sans-serif;
  color: #181d27;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  box-sizing: border-box;
}

.message-search input:focus {
  border-color: #3467d5;
  box-shadow: 0 0 0 3px rgba(52, 103, 213, 0.10);
}

.message-search .search-icon {
  position: absolute;
  left: 24px;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  pointer-events: none;
}

/* .filter-sec {
  max-width: 1440px;
  width: 100%;
  margin-top: 64px;
  margin-left: 80px;
  margin-right: 80px;
  margin-bottom: 64px;
    margin: 64px auto;  
  padding: 0 80px;     
} */
.filter-sec {
  max-width: 1440px;
  width: 100%;
  margin: 64px auto; /* centers perfectly with 64px top & bottom */
  padding: 0 80px; /* same inner spacing as navbar */
  box-sizing: border-box;
}

.filter-bar {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 1280px;
  width: 100%;
  margin: 0 auto;
  /* padding: 0 32px;    */
  justify-content: center;
  box-sizing: border-box;
}

.sec-title {
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 40px;
  color: #111827;
  font-family: "Inter", sans-serif;
  letter-spacing: -2%;
  line-height: 32px;
}

.filter-sec .filter-row {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 21px;
  flex-wrap: nowrap;
  width: 100%;
}
.message-badge {
  width: 32px;
  height: 22px;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  padding: 0px !important;
}
.message-badge p {
  padding-right: 3px;
  padding-left: 3px;
  text-align: center;
}

.message-title {
  display: flex;
  gap: 8px;
  align-items: center;
}
.message-title p {
  margin: 0px !important;
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  font-family: "Inter", sans-serif;
  line-height: 22px;
}
.message-badge p {
  margin: 0px !important;
  font-size: 12px;
  font-weight: 500;
  color: #414651;
  font-family: "Inter", sans-serif;
  line-height: 18px;
}
.message-container {
  width: 100%;
  height: 740px;
  border-radius: 18px;
  border: 1px solid #e5e9f2;
  padding: 0;
  display: flex;
  max-width: 1440px;
  margin: 0 auto;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(52, 103, 213, 0.07), 0 1px 4px rgba(10, 13, 40, 0.05);
  background: #fff;
}
.message-list {
  width: 320px;
  min-width: 280px;
  height: 740px;
  border-right: 1px solid #f0f1f5;
  padding: 0 0 8px;
  background: #fafbff;
}
.message-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: auto;
  padding: 16px 16px 12px;
  border-bottom: 1px solid #f0f1f5;
  box-sizing: border-box;
}

.filter-row > div {
  flex: 1;
  min-width: 0;
  flex: 0 0 auto;
}

.filter-row.top-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* old styles */

.contact-list {
  margin-top: 8px;
  overflow-y: auto;
  max-height: calc(740px - 130px);
  padding: 0 8px 8px;
  scrollbar-width: none;
}

.contact-list::-webkit-scrollbar {
  display: none;
}

.contact {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;
  position: relative;
  margin-bottom: 2px;
}

.contact:hover {
  background-color: #f0f4ff;
}

.contact.has-unread {
  background-color: #eef3ff;
  border-left: 3px solid #3467d5;
  padding-left: 10px;
}

.contact.selected {
  background-color: #e8eeff !important;
  border: 1px solid #c7d6ff !important;
  border-radius: 10px;
  box-shadow: none;
}

.avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  object-fit: cover;
}

.avatar-container {
  position: relative;
  display: flex;
  align-items: center;
  margin-right: 10px;
  flex-shrink: 0;
}

.status-dot {
  position: absolute;
  bottom: 2px;
  right: -1px;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  border: 2px solid #fafbff;
}

/* Scoped to the dot on purpose. As bare `.online` / `.offline` these also hit
   .online-status in the chat header — same specificity, further down the file,
   so they won — and painted the status badge a solid dot-green or dot-grey
   block instead of leaving it as a caption. */
.status-dot.online {
  background-color: #22c55e;
}
.status-dot.offline {
  background-color: #d1d5db;
}

.contact-info h4 {
  position: relative;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 2px;
  font-family: "Inter", sans-serif;
  transform: translateY(-2px);
  line-height: 20px;
}

.contact-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-grow: 1;
  padding-right: 10px;
}
.contact-info p {
  font-size: 13px;
  color: #555;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
}

/* Fade-in animation when appearing */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.scroll-btn.v-show {
  animation: fadeIn 0.3s ease-in-out;
}
.icon-btn:hover,
.icon-btn:focus,
.icon-btn:active {
  background: none;
  border: none;
  outline: none;
}

.chat-header {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 14px 20px;
  border-bottom: 1px solid #f0f1f5;
  box-sizing: border-box;
  background: #fff;
}
.chat-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
/* Every <p> in the header: kill the UA margin. .select-username kept 14px of
   default margin above and below, which drove the header's height (77px for a
   42px avatar) and left the whole cluster sitting off its centre. The old
   margin-right also stacked on the header's 10px gap, so the name-to-status
   spacing was 18px while everything else was 10px. */
.chat-header p {
  font-weight: 500;
  font-family: "Inter", sans-serif;
  margin: 0;
}

/* The avatar's own margin is for the contact list, where there is no flex gap;
   in the header it doubles up with one. */
.chat-header .avatar-container {
  margin-right: 0;
}

/* ══════════════════════════════════════════════════════
   CHAT MESSAGE LIST

   Bubbles used to sit flush against the panel edges — the messages had no
   breathing room at all. The list now carries its own padding on a tinted
   surface, the way WhatsApp and Teams both set their threads off from the
   page, and the bubble is one element in both directions.

   Previously .message-outgoing painted the bubble on the flex ROW while
   .message-incoming painted it on .message-content, so the two sides had
   different boxes, different padding and different max-widths. Now the row
   only does alignment and .message-content is always the bubble.
══════════════════════════════════════════════════════ */
.chat-messages {
  overflow-y: auto;
  flex-grow: 1;
  /* No max-height. It used to be calc(100vh - 150px), left over from before
     .message-container had a fixed 740px height. On a short window — or simply
     zoomed in, which shrinks the viewport in CSS pixels — that cap came out
     smaller than the space the flex row had already given this element, and the
     difference showed as a white band between the last message and the input
     box. min-height: 0 is what a scrolling flex child actually needs. */
  min-height: 0;
  padding: 16px 24px 20px;
  background: #f7f9fc;
  /* No scroll-behavior: smooth. scrollToBottom() sets scrollTop directly, and
     smooth turns that into an animation — so opening a thread visibly glides
     from top to bottom, and the position read back straight after is still the
     old one. Chat apps snap. */

  /* Messages sit on the bottom, as in WhatsApp and Teams: a short conversation
     rests just above the input instead of clinging to the top with a large gap
     underneath. margin-top: auto on the first group rather than
     justify-content: flex-end — the latter makes overflowing content
     unreachable above the scroll origin. */
  display: flex;
  flex-direction: column;
}

.chat-messages > *:first-child {
  margin-top: auto;
}

.chat-messages::-webkit-scrollbar {
  width: 8px;
}
.chat-messages::-webkit-scrollbar-thumb {
  background: #d6dce8;
  border-radius: 4px;
}
.chat-messages::-webkit-scrollbar-thumb:hover {
  background: #c2cadb;
}

/* Centred pill, tinted rather than white so it reads as a divider on the
   tinted surface instead of vanishing into it. */
.date-separator {
  text-align: center;
  font-size: 11px;
  letter-spacing: 0.02em;
  color: #5b6880;
  font-weight: 600;
  background: #e9eef7;
  padding: 5px 14px;
  border-radius: 999px;
  display: block;
  width: fit-content;
  margin: 18px auto 14px;
}

.message {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  /* Tight inside a run, roomy between runs — the rhythm that makes a thread
     scannable. .run-end opens the gap before the next person speaks. */
  margin: 2px 0;
}

.message.run-end {
  margin-bottom: 12px;
}

.message .avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  /* Top of the run's first bubble, level with its tail, the way Teams anchors
     it — flush with the bottom would leave it stranded mid-run. */
  align-self: flex-start;
}

.avatar-spacer {
  width: 28px;
  flex-shrink: 0;
}

.message-incoming {
  justify-content: flex-start;
}

.message-outgoing {
  justify-content: flex-end;
}

/* The bubble. */
.message-content {
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  max-width: 68%;
  min-width: 0;
  padding: 8px 12px 6px;
  border-radius: 14px;
  box-shadow: 0 1px 1px rgba(15, 30, 60, 0.06);
}

.message-incoming .message-content {
  background: #ffffff;
  color: #1f2a3d;
  border: 1px solid #e8edf5;
}

.message-outgoing .message-content {
  background: #3467d5;
  color: #ffffff;
}

/* The tail: the first bubble of a run squares off the corner facing its
   speaker, so a run reads as one block with a single point of origin. */
.message-incoming.run-start .message-content {
  border-top-left-radius: 4px;
}

.message-outgoing.run-start .message-content {
  border-top-right-radius: 4px;
}

.message-text {
  word-break: break-word;
  white-space: pre-wrap;
}

.message-text :deep(a) {
  color: inherit;
  text-decoration: underline;
}

/* Time and ticks ride at the bottom-right inside the bubble, small and quiet,
   as in both apps. */
.message-meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  margin-top: 2px;
  line-height: 1;
}

.message-time {
  font-size: 10px;
  display: inline-block;
}

.message-incoming .message-time {
  color: #8b95a7;
}

.message-outgoing .message-time {
  color: rgba(255, 255, 255, 0.75);
}

.message-ticks {
  font-size: 11px;
  letter-spacing: -1px;
  color: rgba(255, 255, 255, 0.75);
  user-select: none;
}
.message-ticks.delivered {
  color: rgba(255, 255, 255, 0.9);
}
.message-ticks.read {
  color: #8ed2ff;
}

@media (max-width: 768px) {
  .chat-messages {
    padding: 12px 12px 16px;
  }
  .message-content {
    max-width: 80%;
  }
}
</style>
